import { useCallback, useEffect, useMemo, useState } from 'react'
import { AccountLink, DrillValue } from '../../components/Drill'
import { checkEmailDelivery, getEmailLog, type EmailLog, type EmailLogRow } from '../../lib/adminClient'
import { useTableControls, type Column } from '../../lib/useTableControls'
import { NoMatches, TableHead, TableToolbar } from '../../components/TableControls'
import { LoadError } from '../../components/LoadError'
import { dateTime, day, num } from '../../lib/format'

// Every email the server sent, newest first (server: emailSendLog.ts). Owner,
// 2026-10-30: "a list of emails sent, and who they were sent to that is
// filterable by all data". Every column filters (a value list, or a contains
// box for free text), the search box looks across all of them, and the CSV is
// exactly the rows the filters leave.

const RANGES: Array<{ value: number | 'all'; label: string }> = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 365, label: 'Last 12 months' },
  { value: 'all', label: 'All time' }
]

const PAGE = 200

const COLUMNS: Column<EmailLogRow>[] = [
  { key: 'at', label: 'When', value: (r) => dateTime(r.at), filter: 'text' },
  { key: 'to', label: 'To', value: (r) => r.to, filter: 'text' },
  { key: 'account', label: 'Account', value: (r) => (r.toAccountId ? r.toName ?? 'Account, no name' : 'No account'), filter: 'text' },
  { key: 'label', label: 'Email', value: (r) => r.label },
  { key: 'category', label: 'Kind', value: (r) => r.category },
  { key: 'subject', label: 'Subject', value: (r) => r.subject || '(not recorded)', filter: 'text' },
  { key: 'status', label: 'Status', value: (r) => r.status },
  { key: 'sentBy', label: 'Sent by', value: (r) => r.sentBy },
  { key: 'campaign', label: 'Console send', value: (r) => r.campaignSubject ?? '' },
  { key: 'reason', label: 'Reason', value: (r) => r.reason ?? '', filter: 'text' },
  { key: 'provider', label: 'Provider', value: (r) => r.provider }
]

const STATUS_TONE: Record<string, string> = {
  Sent: 'bg-emerald-500/15 text-emerald-200',
  Failed: 'bg-red-500/15 text-red-200',
  'Not sent': 'bg-amber-500/15 text-amber-200',
  'Not confirmed': 'bg-white/5 text-slate-300'
}

// What Resend's last_event means, in words.
const DELIVERY_WORDS: Record<string, string> = {
  queued: 'Queued at Resend, not handed on yet.',
  scheduled: 'Scheduled at Resend.',
  sent: 'Accepted by Resend; no delivery confirmed yet.',
  delivered: 'Delivered to their mail server.',
  delivery_delayed: 'Delivery delayed; Resend is still trying.',
  bounced: 'Bounced: their mail server did not accept it.',
  complained: 'They marked it as spam.',
  opened: 'Delivered, and opened.',
  clicked: 'Delivered, and a link in it was clicked.',
  canceled: 'Cancelled at Resend before it went.',
  failed: 'Failed at Resend.'
}

// A cell starting with = + - @ is a formula to a spreadsheet. Subjects and
// addresses are partly typed by users, so each such cell is quoted as text.
function csvCell(v: string): string {
  const safe = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v
  return `"${safe.replace(/"/g, '""')}"`
}

function toCsv(rows: EmailLogRow[]): string {
  const head = ['When (UTC)', 'To', 'Account', 'Email', 'Kind', 'Subject', 'Status', 'Reason', 'Sent by', 'Console send', 'Provider', 'Provider message id', 'Log id']
  const lines = rows.map((r) =>
    [
      new Date(r.at).toISOString(),
      r.to,
      r.toAccountId ? r.toName ?? '' : '',
      r.label,
      r.category,
      r.subject,
      r.status,
      r.reason ?? '',
      r.sentBy,
      r.campaignSubject ?? '',
      r.provider,
      r.providerMessageId ?? '',
      r.id
    ]
      .map(csvCell)
      .join(',')
  )
  return [head.map(csvCell).join(','), ...lines].join('\r\n')
}

type LoadState = { s: 'loading' } | { s: 'ready'; log: EmailLog } | { s: 'error'; error: string }

export default function SentLog({ onOpenCampaign, initialSearch }: { onOpenCampaign: (id: string) => void; initialSearch?: string | null }): JSX.Element {
  const [range, setRange] = useState<number | 'all'>(90)
  const [state, setState] = useState<LoadState>({ s: 'loading' })
  const [shown, setShown] = useState(PAGE)
  const [open, setOpen] = useState<string | null>(null)

  const load = useCallback(async () => {
    setState({ s: 'loading' })
    const res = await getEmailLog(range)
    setState(res.ok ? { s: 'ready', log: res.data } : { s: 'error', error: res.error })
  }, [range])
  useEffect(() => { void load() }, [load])

  const rows = state.s === 'ready' ? state.log.rows : null
  const t = useTableControls(rows, COLUMNS, 'email-log')
  // Arriving from a person's "Emails sent to them": search for their address.
  const { setSearch } = t
  useEffect(() => {
    if (initialSearch) setSearch(initialSearch)
  }, [initialSearch, setSearch])
  useEffect(() => setShown(PAGE), [t.rows])

  const counts = useMemo(() => {
    const c = { Sent: 0, Failed: 0, other: 0 }
    for (const r of t.rows) {
      if (r.status === 'Sent') c.Sent++
      else if (r.status === 'Failed') c.Failed++
      else c.other++
    }
    return c
  }, [t.rows])

  function downloadCsv(): void {
    const blob = new Blob([toCsv(t.rows)], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `plexii-sent-emails-${day(Date.now())}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select
          value={String(range)}
          onChange={(e) => setRange(e.target.value === 'all' ? 'all' : Number(e.target.value))}
          className="rounded-md bg-white/[0.03] border border-white/10 px-2 py-1.5 text-sm text-slate-200"
          data-testid="log-range"
          aria-label="Date range"
        >
          {RANGES.map((r) => (
            <option key={String(r.value)} value={String(r.value)}>{r.label}</option>
          ))}
        </select>
        <button className="btn-ghost text-xs" onClick={() => void load()} data-testid="log-refresh">Refresh</button>
        {state.s === 'ready' && state.log.coverage.completeSince && (
          <span className="text-xs text-slate-400" title={state.log.coverage.note} data-testid="log-coverage">
            Every email is recorded from {day(state.log.coverage.completeSince)}. Earlier ones are partial.
          </span>
        )}
      </div>

      {state.s === 'loading' && <div className="glass-card rounded-xl p-6 text-sm text-slate-400">Loading the log…</div>}
      {state.s === 'error' && <LoadError what="the sent log" endpoint="GET /admin/emails/log" error={state.error} testId="log-error" />}

      {state.s === 'ready' && (
        <>
          {state.log.truncated && (
            <div className="glass-card rounded-xl p-3 mb-3 text-sm text-amber-200 border border-amber-400/20" data-testid="log-truncated">
              This range holds {num(state.log.total)} emails; the newest {num(state.log.rows.length)} are loaded. Pick a shorter range to filter
              the rest.
            </div>
          )}
          <TableToolbar t={t} placeholder="Search address, name, subject, reason…" noun="emails">
            <span className="text-[11px] text-slate-400 tabular-nums" data-testid="log-tally">
              {num(counts.Sent)} sent · {num(counts.Failed)} failed · {num(counts.other)} not sent or not confirmed
            </span>
            <button className="btn-ghost text-xs" onClick={downloadCsv} disabled={t.rows.length === 0} data-testid="log-csv">
              Download CSV
            </button>
          </TableToolbar>

          {state.log.rows.length === 0 ? (
            <div className="glass-card rounded-xl p-6 text-sm text-slate-400" data-testid="log-empty">No emails were sent in this range.</div>
          ) : t.rows.length === 0 ? (
            <NoMatches t={t} noun="emails" />
          ) : (
            <div className="glass-card rounded-xl overflow-x-auto">
              <table className="admin-table" data-testid="log-table">
                <TableHead t={t} />
                <tbody>
                  {t.rows.slice(0, shown).map((r) => (
                    <LogRow key={r.id} r={r} open={open === r.id} onToggle={() => setOpen(open === r.id ? null : r.id)} onOpenCampaign={onOpenCampaign} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {t.rows.length > shown && (
            <div className="mt-3 flex items-center gap-3 text-xs text-slate-400">
              <span>Showing {num(shown)} of {num(t.rows.length)}.</span>
              <button className="btn-ghost text-xs" onClick={() => setShown(shown + PAGE)} data-testid="log-more">Show {PAGE} more</button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function LogRow({ r, open, onToggle, onOpenCampaign }: { r: EmailLogRow; open: boolean; onToggle: () => void; onOpenCampaign: (id: string) => void }): JSX.Element {
  const [delivery, setDelivery] = useState<{ busy: boolean; text: string | null }>({ busy: false, text: null })

  async function lookUp(): Promise<void> {
    setDelivery({ busy: true, text: null })
    const res = await checkEmailDelivery(r.id)
    if (!res.ok) return setDelivery({ busy: false, text: res.error })
    const d = res.data
    setDelivery({ busy: false, text: d.known ? DELIVERY_WORDS[d.lastEvent ?? ''] ?? `Resend says: ${d.lastEvent ?? 'nothing yet'}.` : d.message ?? 'Unknown.' })
  }

  return (
    <>
      <tr className="cursor-pointer hover:bg-white/[0.02]" onClick={onToggle} data-testid={`log-row-${r.id}`}>
        <td className="whitespace-nowrap text-xs text-slate-400 tabular-nums">{dateTime(r.at)}</td>
        <td className="text-slate-200 whitespace-nowrap">{r.to}</td>
        <td className="text-xs">{r.toAccountId ? <span className="text-slate-200">{r.toName ?? 'Account, no name'}</span> : <span className="text-slate-500">No account</span>}</td>
        <td className="text-xs text-slate-200 whitespace-nowrap">{r.label}</td>
        <td className="text-xs text-slate-400 whitespace-nowrap">{r.category}</td>
        <td className="text-xs text-slate-300 max-w-[18rem] truncate" title={r.subject}>{r.subject || <span className="text-slate-500">(not recorded)</span>}</td>
        <td>
          <span className={`text-[11px] px-1.5 py-0.5 rounded whitespace-nowrap ${STATUS_TONE[r.status] ?? 'bg-white/5 text-slate-300'}`} data-testid={`log-status-${r.id}`}>
            {r.status}
          </span>
        </td>
        <td className="text-xs text-slate-400">{r.sentBy}</td>
        <td className="text-xs text-slate-400 max-w-[12rem] truncate" title={r.campaignSubject ?? ''}>{r.campaignSubject ?? ''}</td>
        <td className="text-xs text-slate-400 max-w-[14rem] truncate" title={r.reason ?? ''}>{r.reason ?? ''}</td>
        <td className="text-xs text-slate-500">{r.provider}</td>
      </tr>
      {open && (
        <tr data-testid={`log-detail-${r.id}`}>
          <td colSpan={COLUMNS.length} className="bg-white/[0.02]">
            <div className="p-3 grid gap-2 text-xs text-slate-300 sm:grid-cols-2">
              <div><span className="text-slate-500">Subject: </span>{r.subject || '(not recorded)'}</div>
              <div><span className="text-slate-500">To: </span><AccountLink id={r.toAccountId}>{r.to}</AccountLink>{r.toName ? ` (${r.toName})` : ''}</div>
              {r.reason && <div className="sm:col-span-2"><span className="text-slate-500">Why: </span>{r.reason}</div>}
              <div className="break-all"><span className="text-slate-500">Provider message id: </span>{r.providerMessageId ?? 'none'}</div>
              <div className="break-all">
                <span className="text-slate-500">Log id: </span>
                <DrillValue to={{ set: 'emails', filters: { id: r.id } }} label={`Email to ${r.to}`} testId={`log-record-${r.id}`}>{r.id}</DrillValue>
              </div>
              <div className="sm:col-span-2 flex flex-wrap items-center gap-2 pt-1">
                {r.providerMessageId && (
                  <button className="btn-ghost text-xs" onClick={(e) => { e.stopPropagation(); void lookUp() }} disabled={delivery.busy} data-testid={`log-delivery-${r.id}`}>
                    {delivery.busy ? 'Asking Resend…' : 'Check delivery'}
                  </button>
                )}
                {r.campaignId && (
                  <button className="btn-ghost text-xs" onClick={(e) => { e.stopPropagation(); onOpenCampaign(r.campaignId!) }} data-testid={`log-open-send-${r.id}`}>
                    Open the send
                  </button>
                )}
                {delivery.text && <span className="text-slate-200" data-testid={`log-delivery-result-${r.id}`}>{delivery.text}</span>}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}
